import { useCallback, useEffect, useState } from "react";

import {
  getReportStrategyComposedPinescript,
  getReportStrategyDetail,
  getReportStrategyIndicatorPinescript,
  listReportStrategyPolicyVersions,
  ReportStrategyComposedPineError,
} from "../../../services/reportsApi";
import type { ComposeOutputMode } from "../../../types/reportStrategyPine";
import type {
  ReportStrategyDetailResponse,
  ReportStrategyPolicyVersion,
  ReportStrategyResponse,
} from "../../../types/reports";
import {
  activePineStatusLabel,
  canDownloadActiveComposedPine,
  extractPineScriptText,
  filterPolicyVersionsForCompose,
  isSeparateIndicatorComposeMode,
  loadStoredComposeMode,
  strategyHasMasterPinescriptUploaded,
  strategyHasTradingViewPinescriptCreated,
} from "../../../utils/reportStrategyPineUi";

import {
  WORKSPACE_PINE_ACTIVE_REMOVED,
  WORKSPACE_PINE_ACTIVE_SHOW,
  WORKSPACE_PINE_COMPOSE_FAILED,
  WORKSPACE_PINE_COMPOSE_REFRESH,
  WORKSPACE_PINE_COMPOSE_START,
  WORKSPACE_PINE_MASTER_SHOW,
  dispatchWorkspacePineDockVisibility,
  type WorkspacePineComposeEventDetail,
} from "./workspacePineComposeEvents";

export type WorkspaceStrategyPineState = {
  strategyId: string | null;
  strategyTitle: string | null;
  strategy: ReportStrategyResponse | null;
  composeVersions: ReportStrategyPolicyVersion[];
  currentPolicyVersionId: string | null;
  loading: boolean;
  error: string | null;
  /** True when the strategy has an uploaded master Pinescript to show. */
  showMaster: boolean;
  /** True when the strategy has a composed/stale active TradingView Pinescript to show. */
  showActive: boolean;
  /** True while compose is in-flight or the dock scramble reveal is finishing. */
  composeSessionActive: boolean;
  composeSequence: number;
  masterCode: string;
  masterEmptyMessage: string | null;
  activeCode: string;
  activeIndicatorCode: string;
  activeEmptyMessage: string | null;
  activeStatusLabel: string | null;
  activeStale: boolean;
  refresh: () => void;
  endComposeSession: () => void;
  hideMaster: () => void;
  hideActive: () => void;
};

const EMPTY_STATE: Omit<
  WorkspaceStrategyPineState,
  "refresh" | "endComposeSession" | "hideMaster" | "hideActive"
> = {
  strategyId: null,
  strategyTitle: null,
  strategy: null,
  composeVersions: [],
  currentPolicyVersionId: null,
  loading: false,
  error: null,
  showMaster: false,
  showActive: false,
  composeSessionActive: false,
  composeSequence: 0,
  masterCode: "",
  masterEmptyMessage: null,
  activeCode: "",
  activeIndicatorCode: "",
  activeEmptyMessage: null,
  activeStatusLabel: null,
  activeStale: false,
};

function strategyDisplayTitle(detail: ReportStrategyDetailResponse): string {
  const name = detail.name.trim();
  return name || "Strategy";
}

function normalizePolicyVersions(
  data: Awaited<ReturnType<typeof listReportStrategyPolicyVersions>>
): ReportStrategyPolicyVersion[] {
  const list = Array.isArray((data as { versions?: unknown })?.versions)
    ? (data as { versions: ReportStrategyPolicyVersion[] }).versions
    : Array.isArray(data)
      ? (data as ReportStrategyPolicyVersion[])
      : [];
  return list;
}

export function useWorkspaceStrategyPine(
  strategyEntityId: string | null
): WorkspaceStrategyPineState {
  const [state, setState] = useState<
    Omit<WorkspaceStrategyPineState, "refresh" | "endComposeSession" | "hideMaster" | "hideActive">
  >(EMPTY_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  /** Master/Active stay hidden until the strategy sheet explicitly reveals them. */
  const [masterHidden, setMasterHidden] = useState(true);
  const [activeHidden, setActiveHidden] = useState(true);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const endComposeSession = useCallback(() => {
    setState((prev) => ({
      ...prev,
      composeSessionActive: false,
    }));
  }, []);

  const beginComposeSession = useCallback(() => {
    setActiveHidden(false);
    setState((prev) => ({
      ...prev,
      composeSessionActive: true,
      composeSequence: prev.composeSequence + 1,
    }));
  }, []);

  const hideMaster = useCallback(() => {
    setMasterHidden(true);
  }, []);

  const hideActive = useCallback(() => {
    setActiveHidden(true);
  }, []);

  useEffect(() => {
    setMasterHidden(true);
    setActiveHidden(true);
  }, [strategyEntityId]);

  useEffect(() => {
    if (!strategyEntityId) return;

    const strategyId = strategyEntityId.trim();
    if (!strategyId) return;

    const matches = (event: Event): boolean => {
      const detail = (event as CustomEvent<WorkspacePineComposeEventDetail>).detail;
      return detail?.strategyId === strategyId;
    };

    const onComposeStart = (event: Event) => {
      if (!matches(event)) return;
      beginComposeSession();
    };

    const onComposeFailed = (event: Event) => {
      if (!matches(event)) return;
      endComposeSession();
    };

    const onComposeRefresh = (event: Event) => {
      if (!matches(event)) return;
      refresh();
    };

    const onActiveRemoved = (event: Event) => {
      if (!matches(event)) return;
      setState((prev) => ({
        ...prev,
        showActive: false,
        activeCode: "",
        activeIndicatorCode: "",
        activeEmptyMessage: null,
        activeStatusLabel: null,
        activeStale: false,
      }));
      setActiveHidden(true);
    };

    const onMasterShow = (event: Event) => {
      if (!matches(event)) return;
      setMasterHidden(false);
    };

    const onActiveShow = (event: Event) => {
      if (!matches(event)) return;
      setActiveHidden(false);
    };

    window.addEventListener(WORKSPACE_PINE_COMPOSE_START, onComposeStart);
    window.addEventListener(WORKSPACE_PINE_COMPOSE_FAILED, onComposeFailed);
    window.addEventListener(WORKSPACE_PINE_COMPOSE_REFRESH, onComposeRefresh);
    window.addEventListener(WORKSPACE_PINE_ACTIVE_REMOVED, onActiveRemoved);
    window.addEventListener(WORKSPACE_PINE_MASTER_SHOW, onMasterShow);
    window.addEventListener(WORKSPACE_PINE_ACTIVE_SHOW, onActiveShow);
    return () => {
      window.removeEventListener(WORKSPACE_PINE_COMPOSE_START, onComposeStart);
      window.removeEventListener(WORKSPACE_PINE_COMPOSE_FAILED, onComposeFailed);
      window.removeEventListener(WORKSPACE_PINE_COMPOSE_REFRESH, onComposeRefresh);
      window.removeEventListener(WORKSPACE_PINE_ACTIVE_REMOVED, onActiveRemoved);
      window.removeEventListener(WORKSPACE_PINE_MASTER_SHOW, onMasterShow);
      window.removeEventListener(WORKSPACE_PINE_ACTIVE_SHOW, onActiveShow);
    };
  }, [beginComposeSession, endComposeSession, refresh, strategyEntityId]);

  useEffect(() => {
    if (!strategyEntityId) {
      setState(EMPTY_STATE);
      return;
    }

    let cancelled = false;
    const strategyId = strategyEntityId.trim();
    if (!strategyId) {
      setState(EMPTY_STATE);
      return;
    }

    setState((prev) => {
      if (prev.strategyId === strategyId) {
        return {
          ...prev,
          loading: true,
          error: null,
        };
      }

      return {
        ...EMPTY_STATE,
        strategyId,
        loading: true,
      };
    });

    void (async () => {
      try {
        const [detail, versionsResponse] = await Promise.all([
          getReportStrategyDetail(strategyId),
          listReportStrategyPolicyVersions(strategyId, { include_archived: true }),
        ]);
        if (cancelled) return;

        const versions = normalizePolicyVersions(versionsResponse);
        const composeVersions = filterPolicyVersionsForCompose(versions, false);
        const currentPolicyVersionId =
          detail.current_policy_version_id?.trim() ||
          versions.find((version) => version.is_current)?.id?.trim() ||
          null;

        const showMaster = strategyHasMasterPinescriptUploaded(detail);
        const showActive = strategyHasTradingViewPinescriptCreated(detail);

        const masterCode = extractPineScriptText(detail).trim();
        const masterEmptyMessage = showMaster
          ? masterCode
            ? null
            : "Master Pinescript is uploaded but the source body was not returned."
          : null;

        const activeStatus = detail.active_pine?.status ?? "none";
        const activeStale =
          activeStatus === "stale" || Boolean(detail.active_pine?.compose_stale);
        const activeStatusLabel = activePineStatusLabel(detail.active_pine);
        const hasGenerated = canDownloadActiveComposedPine(detail);

        // Commit dock panel layout as soon as strategy metadata is known so
        // master-only strategies do not flash from a 2-column left slot to right.
        if (!cancelled) {
          setState((prev) => ({
            strategyId,
            strategyTitle: strategyDisplayTitle(detail),
            strategy: detail,
            composeVersions,
            currentPolicyVersionId,
            loading: true,
            error: null,
            showMaster,
            showActive: showActive || prev.composeSessionActive,
            composeSessionActive: prev.composeSessionActive,
            composeSequence: prev.composeSequence,
            masterCode,
            masterEmptyMessage,
            activeCode: prev.composeSessionActive ? prev.activeCode : "",
            activeIndicatorCode: prev.composeSessionActive ? prev.activeIndicatorCode : "",
            activeEmptyMessage: null,
            activeStatusLabel,
            activeStale,
          }));
        }

        let activeCode = "";
        let activeIndicatorCode = "";
        let activeEmptyMessage: string | null = null;
        let activeError: string | null = null;

        if (showActive) {
          if (activeStale) {
            activeEmptyMessage =
              "Active Pinescript is outdated. Re-compose from the strategy drawer to refresh it.";
          } else if (!hasGenerated) {
            activeEmptyMessage = "Active Pinescript is not available to download right now.";
          } else {
            try {
              const composed = await getReportStrategyComposedPinescript(strategyId);
              if (cancelled) return;
              activeCode = extractPineScriptText(composed).trim();
              const outputMode =
                (composed.meta?.output_mode as ComposeOutputMode | undefined) ??
                (detail.active_pine?.last_compose_mode as ComposeOutputMode | undefined) ??
                loadStoredComposeMode(strategyId) ??
                undefined;
              const needsIndicator =
                isSeparateIndicatorComposeMode(outputMode) || Boolean(detail.has_indicator_pine);
              if (needsIndicator) {
                try {
                  const indicator = await getReportStrategyIndicatorPinescript(strategyId);
                  if (cancelled) return;
                  activeIndicatorCode = extractPineScriptText(indicator).trim();
                } catch (err) {
                  if (
                    err instanceof ReportStrategyComposedPineError &&
                    err.code === "no_composed_pine"
                  ) {
                    activeError =
                      "Strategy snapshot loaded, but the Tessera indicator overlay is missing.";
                  }
                }
              }
              if (!activeCode && !activeIndicatorCode && !activeError) {
                activeEmptyMessage = "Composed Pinescript response was empty.";
              }
            } catch (err) {
              activeError =
                err instanceof Error ? err.message : "Failed to load active Pinescript.";
            }
          }
        }

        if (cancelled) return;
        setState((prev) => ({
          strategyId,
          strategyTitle: strategyDisplayTitle(detail),
          strategy: detail,
          composeVersions,
          currentPolicyVersionId,
          loading: false,
          error: activeError,
          showMaster,
          showActive,
          composeSessionActive: prev.composeSessionActive,
          composeSequence: prev.composeSequence,
          masterCode,
          masterEmptyMessage,
          activeCode,
          activeIndicatorCode,
          activeEmptyMessage,
          activeStatusLabel,
          activeStale,
        }));
      } catch (err) {
        if (cancelled) return;
        setState({
          ...EMPTY_STATE,
          strategyId,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load strategy Pinescript.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadToken, strategyEntityId]);

  const masterVisible = state.showMaster && !masterHidden;
  const activeVisible =
    state.composeSessionActive || (state.showActive && !activeHidden);

  useEffect(() => {
    const strategyId = state.strategyId?.trim();
    if (!strategyId) return;
    dispatchWorkspacePineDockVisibility({
      strategyId,
      masterVisible,
      activeVisible,
    });
  }, [activeVisible, masterVisible, state.strategyId]);

  return {
    ...state,
    showMaster: masterVisible,
    showActive: activeVisible,
    refresh,
    endComposeSession,
    hideMaster,
    hideActive,
  };
}
